import { cloneElement, isValidElement, useState } from "react";
import { formatInr } from "../utils/money";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "sonner";
import { updateRateAndQuantity } from "../apis";
import DeskDialog, { Field, inputClass } from "./DeskDialog";

interface EditProductProps {
  children: React.ReactElement;
  currency: string;
  rate: number;
  quantity: number;
  productId?: string;
  productName?: string;
  oldPayable?: number;
  disabled?: boolean;
}

/**
 * Changing a line's foreign amount or its customer rate. Only what changed is
 * sent, so a blank is never read as zero. A changed rate needs a reason, and
 * core decides who may change it and by how much: its sentence is shown when
 * it refuses. The new total is core's, shown once the order reloads.
 */
export default function EditRateQuantityModal({ children, productName, oldPayable, rate, quantity, productId, disabled }: EditProductProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [rateText, setRateText] = useState("");
  const [qtyText, setQtyText] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const newRate = rateText.trim() === "" ? undefined : Number(rateText);
  const newQty = qtyText.trim() === "" ? undefined : Number(qtyText);
  const rateChanged = newRate !== undefined && Number.isFinite(newRate) && newRate > 0 && Math.abs(newRate - rate) > 0.00005;
  const qtyChanged = newQty !== undefined && Number.isFinite(newQty) && newQty > 0 && newQty !== quantity;
  const invalid =
    (newRate !== undefined && !(Number.isFinite(newRate) && newRate > 0)) ||
    (newQty !== undefined && !(Number.isFinite(newQty) && newQty > 0));
  const ready = !invalid && (rateChanged || qtyChanged) && (!rateChanged || reason.trim().length >= 3);

  const { mutate, isLoading } = useMutation({
    mutationFn: updateRateAndQuantity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orderDetails"] });
      toast.success("Product updated successfully");
      setIsOpen(false);
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "The line could not be changed."),
  });

  const open = () => {
    if (disabled) return;
    setRateText("");
    setQtyText("");
    setReason("");
    setError(null);
    setIsOpen(true);
  };

  return (
    <>
      {isValidElement(children)
        ? cloneElement(children as React.ReactElement<{ onClick?: () => void }>, { onClick: open })
        : children}
      <DeskDialog
        open={isOpen}
        onOpenChange={setIsOpen}
        title={`Edit ${productName ?? "product"}`}
        busy={isLoading}
        submitLabel="Confirm"
        submitDisabled={!ready}
        error={error}
        onSubmit={() => {
          setError(null);
          mutate({
            uuid: productId!,
            fx_amount: qtyChanged ? newQty : undefined,
            rate: rateChanged ? newRate : undefined,
            reason: rateChanged ? reason : undefined,
          });
        }}
      >
        <Field label="Customer rate (INR)" htmlFor="line-rate" hint={`Now ${rate}. Leave blank to keep it.`}>
          <input id="line-rate" className={inputClass} type="number" step="any" min={0} value={rateText} onChange={(e) => setRateText(e.target.value)} placeholder="Enter rate" />
        </Field>
        {rateChanged && (
          <Field label="Reason for changing the rate" htmlFor="line-rate-reason" hint="At least 3 characters. It is kept with the change.">
            <input id="line-rate-reason" className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        )}
        <Field label="Foreign amount" htmlFor="line-qty" hint={`Now ${quantity}. Leave blank to keep it.`}>
          <input id="line-qty" className={inputClass} type="number" min={0} value={qtyText} onChange={(e) => setQtyText(e.target.value)} placeholder="Enter quantity" />
        </Field>
        {oldPayable !== undefined && (
          <p className="text-sm text-[#666666]">The order total is {formatInr(oldPayable)} now. It is worked out again when you save.</p>
        )}
      </DeskDialog>
    </>
  );
}
